/**
 * Ayrshare API Client
 *
 * This module provides API functions for interacting with the Ayrshare integration endpoints.
 * Handles social media connection management and publishing through Ayrshare.
 */

import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../utils/authedFetch';

// Types
export interface SocialConnection {
  platform: string;
  is_connected: boolean;
  username?: string;
  profile_url?: string;
  connected_at?: string;
  /** Days until Ayrshare's scheduled re-auth (LinkedIn/TikTok style); absent elsewhere */
  refresh_days_remaining?: number | null;
  /** Timestamp when the link auth expires, when the platform has one */
  refresh_required?: string | null;
  /** Linked but auth-dead: last publish failed on an auth error, or refresh overdue */
  needs_reconnect?: boolean;
  reconnect_reason?: string | null;
  /** Cooling-off after the network flagged a post as risky (or the manual
      switch): the line is paused until this UTC iso. Null = not cooling. */
  cooling_until?: string | null;
  /** The post-relink hold on a paced channel (TikTok): the line reopens here. */
  relink_hold_until?: string | null;
  cooling_reason?: string | null;
}

/** Editorial channel id → Ayrshare platform id. Channels without an entry
    (quora, newsletter) have no Ayrshare connection concept. Snapchat was
    added by Ayrshare mid-2025 — keep in sync with the backend's
    CHANNEL_TO_AYRSHARE (tendv2_settings.py). */
export const AYRSHARE_PLATFORM_BY_CHANNEL: Record<string, string> = {
  twitter: 'twitter',
  linkedin: 'linkedin',
  instagram: 'instagram',
  facebook: 'facebook',
  threads: 'threads',
  tiktok: 'tiktok',
  youtube_shorts: 'youtube',
  bluesky: 'bluesky',
  reddit: 'reddit',
  pinterest: 'pinterest',
  snapchat: 'snapchat',
};

export interface ConnectionStatusResponse {
  status: string;
  connections: SocialConnection[];
  plan?: string;
  message?: string;
}

export interface ConnectionUrlResponse {
  status: string;
  url?: string;
  error?: string;
}

export interface ProfileResponse {
  status: string;
  profile_key?: string;
  message?: string;
  error?: string;
}

export interface PublishingAnalytics {
  period_days: number;
  total_posts: number;
  successful_posts: number;
  failed_posts: number;
  success_rate: number;
  platform_breakdown: Record<string, { total: number; success: number; failed: number }>;
}

export interface PublishHistoryItem {
  id: number;
  content_id: number;
  ayrshare_id?: string;
  platforms: Record<string, any>;
  status: string;
  published_at?: string;
  created_at?: string;
  error_message?: string;
}

export interface PublishRequest {
  content_id: number;
  platforms?: string[];
}

export interface PublishResponse {
  status: string;
  ayrshare_id?: string;
  platforms: Record<string, any>;
  error?: string;
}

export interface PlatformPublishStatus {
  status: string;
  post_id?: string;
  url?: string;
  error?: string;
  /**
   * How far the network has taken this leg — decided by the backend.
   * 'confirmed' = live with a post id. 'pending' = accepted, no post id yet
   * (TikTok processes after the vendor's 200). 'unconfirmed' = still no post
   * id a day later: check the network. 'failed' = the network said no.
   */
  leg_state?: 'confirmed' | 'pending' | 'unconfirmed' | 'failed' | null;
}

export interface ContentPublishStatus {
  status: string;
  published: boolean;
  ayrshare_id?: string;
  published_at?: string;
  platforms: Record<string, PlatformPublishStatus>;
  /** A deleted post stays published (it went out) and dies there. */
  deleted?: boolean;
  deleted_at?: string | null;
  deleted_via?: 'api' | 'manual' | null;
  /**
   * How this post can be deleted — decided by the backend, never by network
   * name here. 'api' = Delete works from TEND. 'manual' = the network has no
   * API delete (Instagram/TikTok): open the post, remove it there, then
   * "Mark as deleted". null = nothing to delete (not live, or already dead).
   */
  delete_mode?: 'api' | 'manual' | null;
  can_delete: boolean;
  error?: string;
}

export interface DeletePostResponse {
  status: string;
  message?: string;
  error?: string;
  deleted_at?: string;
  deleted_via?: 'api' | 'manual';
}

// Platform display information
export const PLATFORM_INFO: Record<string, { name: string; icon: string; color: string }> = {
  facebook: { name: 'Facebook', icon: '📘', color: '#1877F2' },
  instagram: { name: 'Instagram', icon: '📷', color: '#E4405F' },
  twitter: { name: 'Twitter/X', icon: '🐦', color: '#1DA1F2' },
  linkedin: { name: 'LinkedIn', icon: '💼', color: '#0A66C2' },
  tiktok: { name: 'TikTok', icon: '🎵', color: '#000000' },
  pinterest: { name: 'Pinterest', icon: '📌', color: '#E60023' },
  youtube: { name: 'YouTube', icon: '📺', color: '#FF0000' },
  reddit: { name: 'Reddit', icon: '🔴', color: '#FF4500' },
  gmb: { name: 'Google Business', icon: '🏢', color: '#4285F4' },
  bluesky: { name: 'Bluesky', icon: '🦋', color: '#0085FF' },
  threads: { name: 'Threads', icon: '🧵', color: '#000000' },
  snapchat: { name: 'Snapchat', icon: '👻', color: '#F7F400' },
};

// API Helper — rides the shared authed transport: reads the CURRENT token
// per call (the `token` param is kept for signature compatibility but the
// live stored token always wins — callers used to thread captured tokens
// that went stale after every refresh), and 401s heal via the shared
// refresh-once-and-retry flow.
async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {},
  _token?: string
): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  return authedFetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
}

// API Functions

/**
 * Get social media connection status for a brand
 */
export async function getConnectionStatus(
  brandId: number,
  token?: string
): Promise<ConnectionStatusResponse> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/connections`,
    { method: 'GET' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get connection status');
  }

  return response.json();
}

/**
 * Generate OAuth connection URL for a brand
 */
export async function generateConnectionUrl(
  brandId: number,
  token?: string
): Promise<ConnectionUrlResponse> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/connect`,
    { method: 'POST' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate connection URL');
  }

  return response.json();
}

/**
 * Create Ayrshare profile for a brand
 */
export async function createBrandProfile(
  brandId: number,
  token?: string
): Promise<ProfileResponse> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/profile`,
    { method: 'POST' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create brand profile');
  }

  return response.json();
}

/**
 * Check and cleanup orphaned profile for a brand
 * Called after popup closes to cleanup profiles with no connected accounts
 */
export async function checkAndCleanupProfile(
  brandId: number,
  force: boolean = false,
  token?: string
): Promise<{ status: string; message?: string; has_connections?: boolean }> {
  const params = force ? '?force=true' : '';
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/check-cleanup${params}`,
    { method: 'POST' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to check/cleanup profile');
  }

  return response.json();
}

/**
 * Disconnect a specific platform for a brand
 */
export async function disconnectPlatform(
  brandId: number,
  platform: string,
  token?: string
): Promise<{ status: string; message: string }> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/connections/${platform}`,
    { method: 'DELETE' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to disconnect platform');
  }

  return response.json();
}

/**
 * Get publishing analytics for a brand
 */
export async function getPublishingAnalytics(
  brandId: number,
  days: number = 30,
  token?: string
): Promise<PublishingAnalytics> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/analytics?days=${days}`,
    { method: 'GET' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get publishing analytics');
  }

  return response.json();
}

/**
 * Get publish history for a brand
 */
export async function getPublishHistory(
  brandId: number,
  limit: number = 50,
  offset: number = 0,
  token?: string
): Promise<PublishHistoryItem[]> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/history?limit=${limit}&offset=${offset}`,
    { method: 'GET' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get publish history');
  }

  return response.json();
}

/**
 * Publish TEND content to connected platforms
 */
export async function publishContent(
  brandId: number,
  contentId: number,
  platforms?: string[],
  token?: string
): Promise<PublishResponse> {
  const params = new URLSearchParams();
  params.append('content_id', contentId.toString());
  if (platforms && platforms.length > 0) {
    platforms.forEach(p => params.append('platforms', p));
  }

  // A severed request is NOT a failed publish: nginx cuts upstreams at its
  // proxy timeout while Ayrshare finishes behind it (a 60s+ Facebook
  // firstComment publish 504'd with the post live, 2026-08-24). Errors with
  // no application JSON — network drops, 502/504 gateway pages — are marked
  // maybeStillPublishing so the caller reconciles against publish-status
  // instead of trusting them.
  let response: Response;
  try {
    response = await fetchWithAuth(
      `/tendv2/ayrshare/brands/${brandId}/publish?${params.toString()}`,
      { method: 'POST' },
      token
    );
  } catch (err) {
    const transient = new Error('Publish request did not complete — it may still finish server-side');
    (transient as any).maybeStillPublishing = true;
    throw transient;
  }

  if (!response.ok) {
    let error: any = null;
    try { error = await response.json(); } catch { /* gateway HTML error page */ }
    if (!error || response.status === 502 || response.status === 504) {
      const transient = new Error(
        `Publish request died at the gateway (${response.status}) — it may still finish server-side`
      );
      (transient as any).maybeStillPublishing = true;
      throw transient;
    }
    // Log full error details for debugging (including Ayrshare error codes like 110/duplicate)
    console.error('[Ayrshare] Publish error:', error);
    // Extract error message from nested detail object if present
    const errorMessage = typeof error.detail === 'object'
      ? error.detail.error || JSON.stringify(error.detail)
      : error.detail || 'Failed to publish content';
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Publish multiple content items as a batch
 */
export async function publishBatch(
  brandId: number,
  contentIds: number[],
  platforms?: string[],
  token?: string
): Promise<{ status: string; published: number; failed: number; results: any[] }> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/publish/batch`,
    {
      method: 'POST',
      body: JSON.stringify({
        content_ids: contentIds,
        platforms: platforms,
      }),
    },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to publish batch');
  }

  return response.json();
}

/**
 * Get publishing status for many content items in one request.
 * Returns a map of contentId -> status; every requested id has an entry
 * (status "not_published" when nothing was ever published).
 */
export async function getBulkPublishStatus(
  brandId: number,
  contentIds: number[],
  token?: string
): Promise<Record<string, ContentPublishStatus>> {
  // The endpoint rejects >500 ids; big-brand list views mount every row's
  // button in one batch window, so chunk here — every caller stays safe.
  // A failed chunk throws (the status context falls back to stale cache,
  // never to "not published").
  const CHUNK = 150;
  const chunks: number[][] = [];
  for (let i = 0; i < contentIds.length; i += CHUNK) {
    chunks.push(contentIds.slice(i, i + CHUNK));
  }
  const maps = await Promise.all(chunks.map(async (chunk) => {
    const response = await fetchWithAuth(
      `/tendv2/ayrshare/brands/${brandId}/content/publish-status?content_ids=${chunk.join(',')}`,
      { method: 'GET' },
      token
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get bulk publish status');
    }
    const data = await response.json();
    return (data.statuses || {}) as Record<string, ContentPublishStatus>;
  }));
  return Object.assign({}, ...maps);
}

/**
 * Get publishing status for a specific content item
 * Returns whether content has been published, platform URLs, and delete capability
 */
export async function getContentPublishStatus(
  brandId: number,
  contentId: number,
  token?: string
): Promise<ContentPublishStatus> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/content/${contentId}/publish-status`,
    { method: 'GET' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get publish status');
  }

  return response.json();
}

/**
 * Delete a published post at its network, or — `manual: true` — record that
 * it was removed by hand ("Mark as deleted", for networks with no API
 * delete). Either way the item stays in Published, marked deleted.
 * Uses the Ayrshare post ID returned from publishing.
 */
export async function deletePost(
  brandId: number,
  postId: string,
  token?: string,
  options: { manual?: boolean } = {}
): Promise<DeletePostResponse> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/posts/${postId}${options.manual ? '?manual=true' : ''}`,
    { method: 'DELETE' },
    token
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete post');
  }

  return response.json();
}

/**
 * Manual cooling-off switch for a channel line (TikTok): start one by hand
 * (hours 24/48/72/96, else the brand's cadence setting) or end a running
 * one early. Nothing here talks to the network — it is our own line gate.
 */
export async function setChannelCooldown(
  brandId: number,
  platform: string,
  action: 'start' | 'end',
  hours?: number,
  token?: string
): Promise<{ status: string; cooling: boolean; cooling_until: string | null; message?: string }> {
  const response = await fetchWithAuth(
    `/tendv2/ayrshare/brands/${brandId}/connections/${platform}/cooldown`,
    { method: 'POST', body: JSON.stringify(hours ? { action, hours } : { action }) },
    token
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update cooling-off');
  }

  return response.json();
}

export default {
  getConnectionStatus,
  generateConnectionUrl,
  createBrandProfile,
  checkAndCleanupProfile,
  disconnectPlatform,
  setChannelCooldown,
  getPublishingAnalytics,
  getPublishHistory,
  publishContent,
  publishBatch,
  getBulkPublishStatus,
  getContentPublishStatus,
  deletePost,
  PLATFORM_INFO,
};
